import type { ReactNode } from "react";

import { Calendar, ChevronDown, Clock, Target, Users } from "lucide-react";

import {
  DH_CYCLE_HISTORY_CARD,
  DH_CYCLE_HISTORY_TABLE_WRAP,
  POLL_HISTORY_TABLE,
} from "../../Utils/Helper/discoveryHubUi";

// ── Reusable amber banner ──────────────────────────────────────────────────

export const SampleDataBanner = ({
  message,
  compact = false,
}: {
  message: string;
  /** @deprecated Always one-line; kept for call-site compatibility. */
  oneLine?: boolean;
  /** Smaller badge for card-sized sections (e.g. Executive Summary widgets). */
  compact?: boolean;
}) => (
  <div
    className={`flex min-w-0 max-w-full flex-nowrap items-center justify-center gap-2 rounded-lg border border-amber-300 bg-amber-50 text-center shadow-md shadow-amber-900/10 ${
      compact ? "px-3.5 py-2" : "px-5 py-2.5"
    }`}
  >
    <span
      className={`shrink-0 rounded bg-amber-200 font-bold uppercase tracking-wide text-amber-800 ${
        compact ? "px-1.5 py-0.5 text-[10px]" : "px-1.5 py-0.5 text-xs"
      }`}
    >
      Sample data
    </span>
    <span
      className={`min-w-0 truncate text-amber-800 ${
        compact ? "text-xs leading-snug" : "text-sm leading-snug"
      }`}
    >
      {message}
    </span>
  </div>
);

// ── Reusable sample overlay ─────────────────────────────────────────────────
// Soft 1px blur keeps sample layout readable; centered amber banner marks it
// as preview. Banner is clipped to the overlay so it never spills outside.

export const SampleDataOverlay = ({
  message,
  active = true,
  compact = false,
  className = "",
  children,
}: {
  message: string;
  active?: boolean;
  /** @deprecated Always one-line; kept for call-site compatibility. */
  oneLine?: boolean;
  /** Smaller banner for widgets / half-width cards. */
  compact?: boolean;
  /** Extra classes on the overlay root (e.g. `h-full` to fill a card). */
  className?: string;
  children: ReactNode;
}) => {
  if (!active) {
    return className ? (
      <div className={className}>{children}</div>
    ) : (
      <>{children}</>
    );
  }

  return (
    <div className={`relative min-h-0 ${className}`.trim()}>
      <div className="pointer-events-none h-full select-none blur-[1px] opacity-90">
        {children}
      </div>
      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center overflow-hidden p-3">
        <SampleDataBanner message={message} compact={compact} />
      </div>
    </div>
  );
};

// ── Shared primitive: mimics PollCycleCard header + body ───────────────────

type SampleCardRow = {
  col1: string;
  col2: string;
  col3: string;
  col4: string;
  col4Class?: string;
};

type SampleCardProps = {
  pollName: string;
  countdown?: string;
  period: string;
  dueDate: string;
  poll1?: string;
  poll2?: string;
  col1Header: string;
  col2Header: string;
  rows: SampleCardRow[];
};

const SampleCard = ({
  pollName,
  countdown,
  period,
  dueDate,
  poll1,
  poll2,
  col1Header,
  col2Header,
  rows,
}: SampleCardProps) => (
  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
    {/* mirrors DH_POLL_CYCLE_CARD_HEADER */}
    <div className="p-6">
      <div className="flex items-center gap-3">
        <ChevronDown size={20} className="text-gray-500 shrink-0" />
        <h3 className="text-lg font-medium text-gray-900 mb-0 leading-tight">
          {pollName}
          {countdown && (
            <span className="text-sm font-normal text-gray-500">
              {" "}
              · {countdown}
            </span>
          )}
        </h3>
      </div>
      <div className="flex items-center gap-x-6 gap-y-2 mt-4 text-sm text-gray-600 flex-wrap">
        <span className="flex items-center gap-1">
          <Calendar size={16} />
          <span className="text-gray-500">Period:</span>
          <span className="font-medium text-gray-900">{period}</span>
        </span>
        <span className="flex items-center gap-1">
          <Clock size={16} />
          <span className="text-gray-500">Due:</span>
          <span className="font-medium text-gray-900">{dueDate}</span>
        </span>
        {poll1 && (
          <span className="flex items-center gap-1">
            <Target size={16} />
            <span className="text-gray-500">Poll 1:</span>
            <span className="font-medium text-gray-900">{poll1}</span>
          </span>
        )}
        {poll2 && (
          <span className="flex items-center gap-1">
            <Users size={16} />
            <span className="text-gray-500">Poll 2:</span>
            <span className="font-medium text-gray-900">{poll2}</span>
          </span>
        )}
      </div>
    </div>
    {/* mirrors DH_POLL_CYCLE_CARD_BODY */}
    <div className="border-t border-gray-200 bg-white p-4">
      <table className="w-full border-collapse text-sm [&_th]:bg-gray-50 [&_th]:px-4 [&_th]:py-3 [&_th]:text-xs [&_th]:font-medium [&_th]:text-gray-500 [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-left [&_th]:border-b [&_th]:border-gray-200 [&_td]:px-4 [&_td]:py-3 [&_td]:border-b [&_td]:border-gray-100 [&_tbody_tr:last-child_td]:border-b-0">
        <thead>
          <tr>
            <th>{col1Header}</th>
            <th>{col2Header}</th>
            <th className="text-center">Account Responses</th>
            <th className="text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="font-semibold text-gray-800">{r.col1}</td>
              <td className="text-gray-600">{r.col2}</td>
              <td className="text-center text-gray-600">{r.col3}</td>
              <td className="text-center">
                <span className={r.col4Class ?? "text-gray-600 text-xs"}>
                  {r.col4}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ── Ongoing Polls sample ───────────────────────────────────────────────────

export const SampleOngoingPollCards = () => (
  <div className="flex flex-col gap-5">
    <SampleCard
      pollName="2026-Q3 · Poll 1 — All Sponsors"
      countdown="47 days remaining"
      period="2026-Q3"
      dueDate="Sep 30, 2026"
      poll1="2 / 5"
      col1Header="Sponsor"
      col2Header="Theater"
      rows={[
        {
          col1: "Alex Kim",
          col2: "Americas",
          col3: "0 / 3",
          col4: "Not started",
          col4Class:
            "text-xs font-medium bg-gray-100 text-gray-600 rounded-full px-2 py-0.5",
        },
        {
          col1: "Maria Santos",
          col2: "EMEA",
          col3: "1 / 2",
          col4: "In progress",
          col4Class:
            "text-xs font-medium bg-blue-50 text-blue-700 rounded-full px-2 py-0.5",
        },
        {
          col1: "James Tang",
          col2: "APJ",
          col3: "2 / 2",
          col4: "Complete ✓",
          col4Class:
            "text-xs font-medium bg-green-100 text-green-700 rounded-full px-2 py-0.5",
        },
        {
          col1: "Priya Nair",
          col2: "Americas",
          col3: "0 / 2",
          col4: "Not started",
          col4Class:
            "text-xs font-medium bg-gray-100 text-gray-600 rounded-full px-2 py-0.5",
        },
      ]}
    />
    <SampleCard
      pollName="2026-Q3 · Poll 2 — Team Feedback"
      countdown="47 days remaining"
      period="2026-Q3"
      dueDate="Sep 30, 2026"
      poll2="1 / 4"
      col1Header="Team Member"
      col2Header="Sponsor"
      rows={[
        {
          col1: "Tom Chen",
          col2: "Alex Kim",
          col3: "0 / 3",
          col4: "Not started",
          col4Class:
            "text-xs font-medium bg-gray-100 text-gray-600 rounded-full px-2 py-0.5",
        },
        {
          col1: "Sara Liu",
          col2: "Maria Santos",
          col3: "1 / 2",
          col4: "In progress",
          col4Class:
            "text-xs font-medium bg-blue-50 text-blue-700 rounded-full px-2 py-0.5",
        },
        {
          col1: "Raj Patel",
          col2: "James Tang",
          col3: "2 / 2",
          col4: "Complete ✓",
          col4Class:
            "text-xs font-medium bg-green-100 text-green-700 rounded-full px-2 py-0.5",
        },
      ]}
    />
  </div>
);

// ── Late Polls (Overdue) sample ────────────────────────────────────────────

const OVERDUE_CARD_ROWS = [
  {
    col1: "Alex Kim",
    col2: "Americas",
    col3: "0 / 3",
    col4: "7d overdue",
    col4Class: "text-xs font-bold text-red-700",
  },
  {
    col1: "Chris Brown",
    col2: "APJ",
    col3: "0 / 2",
    col4: "5d overdue",
    col4Class: "text-xs font-bold text-red-700",
  },
  {
    col1: "Lee Park",
    col2: "EMEA",
    col3: "1 / 3",
    col4: "3d overdue",
    col4Class: "text-xs font-bold text-red-700",
  },
];

export const SampleLatePollCards = () => (
  <div className="flex flex-col gap-5">
    <SampleCard
      pollName="2026-Q2 · Poll 1 — All Sponsors"
      countdown="Late window closes in 5 days"
      period="2026-Q2"
      dueDate="Jun 30, 2026"
      poll1="4 / 7"
      col1Header="Sponsor"
      col2Header="Theater"
      rows={OVERDUE_CARD_ROWS}
    />
  </div>
);

// ── Completed Polls sample ─────────────────────────────────────────────────

export const SampleCompletedPollCards = () => (
  <div className="flex flex-col gap-5">
    <SampleCard
      pollName="2026-Q2 · Poll 1 — All Sponsors"
      period="2026-Q2"
      dueDate="Jun 30, 2026"
      poll1="12 / 12"
      col1Header="Sponsor"
      col2Header="Theater"
      rows={[
        {
          col1: "Alex Kim",
          col2: "Americas",
          col3: "3 / 3",
          col4: "Complete ✓",
          col4Class:
            "text-xs font-medium bg-green-100 text-green-700 rounded-full px-2 py-0.5",
        },
        {
          col1: "Maria Santos",
          col2: "EMEA",
          col3: "2 / 2",
          col4: "Complete ✓",
          col4Class:
            "text-xs font-medium bg-green-100 text-green-700 rounded-full px-2 py-0.5",
        },
        {
          col1: "James Tang",
          col2: "APJ",
          col3: "2 / 2",
          col4: "Complete ✓",
          col4Class:
            "text-xs font-medium bg-green-100 text-green-700 rounded-full px-2 py-0.5",
        },
        {
          col1: "Priya Nair",
          col2: "Americas",
          col3: "2 / 2",
          col4: "Complete ✓",
          col4Class:
            "text-xs font-medium bg-green-100 text-green-700 rounded-full px-2 py-0.5",
        },
      ]}
    />
    <SampleCard
      pollName="2026-Q1 · Poll 1 — All Sponsors"
      period="2026-Q1"
      dueDate="Mar 31, 2026"
      poll1="10 / 10"
      col1Header="Sponsor"
      col2Header="Theater"
      rows={[
        {
          col1: "Alex Kim",
          col2: "Americas",
          col3: "3 / 3",
          col4: "Complete ✓",
          col4Class:
            "text-xs font-medium bg-green-100 text-green-700 rounded-full px-2 py-0.5",
        },
        {
          col1: "Maria Santos",
          col2: "EMEA",
          col3: "2 / 2",
          col4: "Complete ✓",
          col4Class:
            "text-xs font-medium bg-green-100 text-green-700 rounded-full px-2 py-0.5",
        },
        {
          col1: "James Tang",
          col2: "APJ",
          col3: "2 / 2",
          col4: "Complete ✓",
          col4Class:
            "text-xs font-medium bg-green-100 text-green-700 rounded-full px-2 py-0.5",
        },
      ]}
    />
  </div>
);

// ── Poll History (Cycle History) sample ───────────────────────────────────

const SAMPLE_CYCLE_ROWS = [
  {
    period: "2026-Q2",
    name: "2026-Q2 · Poll 1 & Poll 2",
    polls_run: "Poll 1 & Poll 2",
    completion: "12 / 12",
    published: "Jun 15, 2026",
    approver: "Sarah Johnson",
  },
  {
    period: "2026-Q1",
    name: "2026-Q1 · Poll 1",
    polls_run: "Poll 1",
    completion: "10 / 12",
    published: "Mar 10, 2026",
    approver: "Sarah Johnson",
  },
  {
    period: "2025-Q4",
    name: "2025-Q4 · Poll 1",
    polls_run: "Poll 1",
    completion: "8 / 10",
    published: "Dec 12, 2025",
    approver: "Sarah Johnson",
  },
];

export const SampleCycleHistoryTable = () => (
  <div className={DH_CYCLE_HISTORY_CARD}>
    <div className={DH_CYCLE_HISTORY_TABLE_WRAP}>
      <table className={POLL_HISTORY_TABLE}>
        <thead>
          <tr>
            <th>Quarter</th>
            <th>Polls run</th>
            <th>Poll 1 completion</th>
            <th>Published on</th>
            <th>Approver</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {SAMPLE_CYCLE_ROWS.map(row => (
            <tr key={row.period}>
              <td>
                <strong>{row.period}</strong>
                <div className="text-sm font-normal text-gray-500 mt-0.5">
                  {row.name}
                </div>
              </td>
              <td>{row.polls_run}</td>
              <td className="tabular-nums">{row.completion}</td>
              <td>{row.published}</td>
              <td>{row.approver}</td>
              <td className="whitespace-nowrap">
                <div className="flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
                    View Responses
                  </span>
                  <span className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
                    View Leaderboard
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
